import moment from "moment";

function toIntValue(value) {
  if (typeof value === "string" && value.length > 0) {
    return parseInt(value)
  }
  return undefined
}

function toIntValues(values) {
  if (typeof values === "string" && values.length > 0) {
    return [parseInt(values)]
  }
  if (values instanceof Array) {
    return values.map(value => parseInt(value))
  }
  return []
}

function convertChatRequest(applicant) {
  if (applicant.request) {
    const job = applicant.request.job
    const requestor = applicant.request.requestor
    return {
      reasons: applicant.request.motivations,
      message: applicant.request.message,
      attractiveSkills: applicant.request.attractiveSkills,
      job: job ? job.id : undefined,
      requestor: requestor ? requestor.id : undefined,
      scoutMotivations: applicant.request.scoutMotivations || [],
      requestedAt: applicant.request.requestedAt,
      byProxy: applicant.request.byProxy
    }
  }
  return null
}

export function convertApplicant(applicant) {
  return {
    id: applicant.id,
    anonymousId: applicant.anonymousId,
    lastName: applicant.detail.lastName,
    firstName: applicant.detail.firstName,
    lastNameKana: applicant.detail.lastNameKana,
    firstNameKana: applicant.detail.firstNameKana,
    joinedAt: applicant.createdAt,
    age: applicant.birthDate ? moment().diff(moment(applicant.birthDate), "years") : undefined,
    salary: applicant.detail.salary,
    changeJobMotivation: applicant.changeJobMotivation,
    lastLoggedInAt: applicant.connectLastAccess,
    favorite: applicant.inFavoriteList,
    listIds: applicant.containedListId,
    currentPositions: applicant.currentJobs || [],
    gender: applicant.gender,
    prefecture: applicant.detail.prefecture,
    countryName: applicant.detail.countryName,
    area: applicant.detail.area,
    isStudent: applicant.isStudent,
    isDisplay: applicant.isDisplay,
    conditions: {
      prefectures: applicant.requirement.location || [],
      remoteWork: applicant.requirement.isRemoteOnly,
      salary: applicant.requirement.annualSalary,
      handsOn: applicant.requirement.handsOn,
      resourceType: applicant.requirement.resourceType,
      developmentType: applicant.requirement.developmentType,
      companyScale: applicant.requirement.companyScale,
      workingStance: applicant.requirement.workingStance,
      hourlyWage: applicant.requirement.hourlyWage,
      workingHoursWeek: applicant.requirement.workingHoursWeek,
      availabilityDaytime: applicant.requirement.availabilityDaytime,
      availabilityEvening: applicant.requirement.availabilityEvening,
      availabilityHoliday: applicant.requirement.availabilityHoliday,
      environment: applicant.requirement.environment,
      otherRequirements: applicant.requirement.otherRequirements,
    },
    competition: applicant.competition,
    languages: applicant.detail.languages.map(lang => ({
      language: lang.language,
      languageLevel: lang.languageLevel,
      otherLanguage: lang.otherLanguage
    })),
    competitionResults: {
      participation: applicant.competitionSummary.totalParticipation,
      posts: applicant.competitionSummary.totalSubmission,
      rank: applicant.competitionSummary.averageRank,
      medals: {
        gold: applicant.competition.medals.gold,
        silver: applicant.competition.medals.silver,
        bronze: applicant.competition.medals.bronze
      },
      histories: applicant.competitions.map(competition => ({
        name: competition.name,
        startAt: competition.startAt,
        closingAt: competition.closingAt,
        rank: competition.rank,
        posts: competition.numberOfSubmissions,
        medal: competition.medal
      }))
    },
    skills: {
      languages: applicant.pr.programmingLanguages || [],
      data: applicant.pr.data || [],
      domain: applicant.pr.domains,
      otherDomain: applicant.pr.otherDomains,
      tools: applicant.pr.tools || [],
      otherTools: applicant.pr.otherTools,
      about: applicant.pr.about
    },
    academic: {
      academicHistory: applicant.detail.academicHistory,
      schoolName: applicant.detail.schoolName,
      schoolDepartment: applicant.detail.department,
      schoolDepartmentDetail: applicant.detail.departmentDetail,
      graduatedYear: applicant.detail.graduatedYear
    },
    careers: applicant.detail.careers.map(career => ({
      companyName: career.company,
      position: career.position,
      startAt: career.startDate,
      endAt: career.endDate,
      employmentStatus: career.employmentStatus,
      jobDetail: career.jobDetail
    })),
    chatRequest: convertChatRequest(applicant),
    signal: applicant.signal
  }
}

function uniq(applicants) {
  return applicants.filter((applicant, index, self) => {
    return self.findIndex(item => applicant.id === item.id) === index
  })
}

export default {
  namespaced: true,
  state: {
    jobs: [],
    tickets: {
      free: undefined,
      paid: undefined
    },
    searchConditionSets: [],
    selectedSearchConditionSet: undefined,
    applicants: [],
    applicantCount: undefined,
    applicant: undefined,
    lists: []
  },
  getters: {
    jobs(state) {
      return state.jobs
    },
    tickets(state) {
      return state.tickets
    },
    searchConditionSets(state) {
      return state.searchConditionSets
    },
    selectedSearchConditionSet(state) {
      return state.selectedSearchConditionSet
    },
    applicants(state) {
      return state.applicants
    },
    applicantCount(state) {
      return state.applicantCount
    },
    applicant(state) {
      return state.applicant
    },
    lists(state) {
      return state.lists
    }
  },
  mutations: {
    setTickets(state, {free, paid}) {
      state.tickets.free = free
      state.tickets.paid = paid
    },
    setJobs(state, {jobs}) {
      state.jobs = jobs.map(job => ({
        id: job.id,
        name: job.name,
        isSide: job.isSide
      }))
    },
    addApplicants(state, {applicants, count}) {
      state.applicants = uniq(state.applicants.concat(applicants))
      state.applicantCount = count
    },
    setApplicants(state, {applicants, count}) {
      state.applicants = uniq(applicants)
      state.applicantCount = count
    },
    setLists(state, {lists}) {
      state.lists = lists.filter(list => !["hidden", "favorite"].includes(list.name))
    },
    addList(state, {list}) {
      state.lists = state.lists.concat({
        id: list.id,
        name: list.name
      })
    },
    setEditList(state, {listId, name}) {
      state.lists = state.lists.map(list => {
        if (list.id === listId) {
          return {...list, name}
        }
        return list
      })
    },
    removeList(state, {listId}) {
      state.lists = state.lists.filter(list => list.id !== listId)
    },
    setSearchConditionSets(state, {conditionsSets}) {
      state.searchConditionSets = conditionsSets
    },
    addSearchConditionSet(state, {conditions}) {
      state.searchConditionSets = state.searchConditionSets.concat(conditions)
    },
    setSearchConditionSet(state, {conditions}) {
      state.searchConditionSets = state.searchConditionSets.map(item => {
        if (item.id === conditions.id) {
          return conditions
        }
        return item
      })
    },
    removeSearchConditionSet(state, {conditionId}) {
      state.searchConditionSets = state.searchConditionSets.filter(item => item.id !== conditionId)
    },
    setSelectedSearchConditionSet(state, {conditionSet}) {
      state.selectedSearchConditionSet = conditionSet
    },
    addApplicantList(state, {userId, listId}) {
      state.applicants = state.applicants.map(applicant => {
        if (applicant.id === userId) {
          return {
            ...applicant,
            listIds: applicant.listIds.concat(listId)
          }
        }
        return applicant
      })
    },
    removeApplicantList(state, {userId, listId}) {
      state.applicants = state.applicants.map(applicant => {
        if (applicant.id === userId) {
          return {
            ...applicant,
            listIds: applicant.listIds.filter(item => item !== listId)
          }
        }
        return applicant
      })
    },
    updateApplicantFavoriteApplicant(state, {userId, favorite}) {
      state.applicants = state.applicants.map(applicant => {
        if (applicant.id === userId) {
          return {
            ...applicant,
            favorite: favorite
          }
        }
        return applicant
      })
    },
    removeApplicant(state, {userId}) {
      state.applicants = state.applicants.filter(applicant => applicant.id !== userId)
    },
    setChatRequest(state, {job, userId, reasons, message, attractiveSkills, requestor, scoutMotivations}) {
      state.applicants = state.applicants.map(applicant => {
        if (applicant.id === userId) {
          return {
            ...applicant,
            chatRequest: {
              job,
              reasons,
              message,
              attractiveSkills,
              requestor,
              scoutMotivations
            }
          }
        }
        return applicant
      })
    },
    setIsDisplay(state, {userId, isDisplay}) {
      state.applicants = state.applicants.map(applicant => {
        if (applicant.id === userId) {
          return {
            ...applicant,
            isDisplay
          }
        }
        return applicant
      })
    }
  },
  actions: {
    updateCompany({commit}, {company}) {
      commit("setTickets", {free: company.freeRequests, paid: company.payRequests})
    },
    updateJobs({commit}, {jobs}) {
      commit("setJobs", {jobs: jobs})
    },
    addSearchConditionSet({commit}, {conditions}) {
      commit("addSearchConditionSet", {conditions: {
          id: conditions.id,
          name: conditions.name,
          conditions: {
            keyword: conditions.conditions.keyword,
            keywordType: conditions.conditions.keywordType,
            data: toIntValues(conditions.conditions.data),
            dataType: conditions.conditions.dataType,
            prefecture: toIntValues(conditions.conditions.location),
            position: toIntValues(conditions.conditions.currentJob),
            salaryLower: toIntValue(conditions.conditions.salaryLower),
            salaryUpper: toIntValue(conditions.conditions.salaryUpper),
            gender: toIntValue(conditions.conditions.gender),
            ageLower: toIntValue(conditions.conditions.ageLower),
            ageUpper: toIntValue(conditions.conditions.ageUpper),
            medalist: conditions.conditions.medalist === "true",
            newcomer: conditions.conditions.newcomer === "true"
          }
        }})
    },
    updateSearchConditionSet({commit}, {conditions}) {
      commit("setSearchConditionSet", {conditions: {
          id: conditions.id,
          name: conditions.name,
          conditions: {
            keyword: conditions.conditions.keyword,
            keywordType: conditions.conditions.keywordType,
            data: toIntValues(conditions.conditions.data),
            dataType: conditions.conditions.dataType,
            prefecture: toIntValues(conditions.conditions.location),
            position: toIntValues(conditions.conditions.currentJob),
            salaryLower: toIntValue(conditions.conditions.salaryLower),
            salaryUpper: toIntValue(conditions.conditions.salaryUpper),
            gender: toIntValue(conditions.conditions.gender),
            ageLower: toIntValue(conditions.conditions.ageLower),
            ageUpper: toIntValue(conditions.conditions.ageUpper),
            medalist: conditions.conditions.medalist === "true",
            newcomer: conditions.conditions.newcomer === "true",
            scoutable: conditions.conditions.scoutable === "true"
          }
        }})
    },
    deleteSearchConditionSet({commit}, {conditionId}) {
      commit("removeSearchConditionSet", {conditionId})
    },
    updateSearchConditionSets({commit}, {conditionsSets}) {
      const andKeyword = (conditions) => {
        if (conditions.andKeyword) {
          return conditions.andKeyword
        }
        if (conditions.keyword && conditions.keywordType === "and") {
          return conditions.keyword
        }
        return undefined
      }
      const orKeyword = (conditions) => {
        if (conditions.orKeyword) {
          return conditions.orKeyword
        }
        if (conditions.keyword && conditions.keywordType === "or") {
          return conditions.keyword
        }
        return undefined
      }
      commit("setSearchConditionSets", {conditionsSets: conditionsSets.map(conditionSet => ({
          id: conditionSet.id,
          name: conditionSet.name,
          conditions: {
            andKeyword: andKeyword(conditionSet.conditions),
            orKeyword: orKeyword(conditionSet.conditions),
            data: toIntValues(conditionSet.conditions.data),
            dataType: conditionSet.conditions.dataType,
            currentPrefecture: toIntValues(conditionSet.conditions.currentLocation),
            prefecture: toIntValues(conditionSet.conditions.location),
            position: toIntValues(conditionSet.conditions.currentJob),
            salaryLower: toIntValue(conditionSet.conditions.salaryLower),
            salaryUpper: toIntValue(conditionSet.conditions.salaryUpper),
            gender: toIntValue(conditionSet.conditions.gender),
            ageLower: toIntValue(conditionSet.conditions.ageLower),
            ageUpper: toIntValue(conditionSet.conditions.ageUpper),
            medalist: conditionSet.conditions.medalist === "true",
            newcomer: conditionSet.conditions.newcomer === "true",
            scoutable: conditionSet.conditions.scoutable === "true"
          }
        }))})
    },
    updateSelectedSearchConditionSet({commit}, {conditionSet}) {
      commit("setSelectedSearchConditionSet", {conditionSet})
    },
    appendApplicants({commit}, {applicants}) {
      commit("addApplicants", {
        applicants: applicants.results.map(applicant => convertApplicant(applicant)),
        count: applicants.count
      })
    },
    updateApplicants({commit}, {applicants}) {
      commit("setApplicants", {
        applicants: applicants.results.map((applicant) => convertApplicant(applicant)),
        count: applicants.count
      })
    },
    updateLists({commit}, {lists}) {
      commit("setLists", {lists: lists.map(list => ({
          id: list.id,
          name: list.name
        }))})
    },
    createList({commit}, {list}) {
      commit("addList", {list})
    },
    editList({commit}, {listId, name}) {
      commit("setEditList", {listId, name})
    },
    deleteList({commit}, {listId}) {
      commit("removeList", {listId})
    },
    addListApplicant({commit}, {userId, listId}) {
      switch(listId) {
        case "favorite":
          commit("updateApplicantFavoriteApplicant", {userId, favorite: true})
          break;
        case "hidden":
          commit("removeApplicant", {userId: userId})
          break;
        default:
          commit("addApplicantList", {userId, listId})
          break;
      }
    },
    deleteListApplicant({commit}, {userId, listId}) {
      switch(listId) {
        case "favorite":
          commit("updateApplicantFavoriteApplicant", {userId, favorite: false})
          break;
        case "hidden":
        case "hotList":
          commit("removeApplicant", {userId: userId})
          break;
        default:
          commit("removeApplicantList", {userId, listId})
          break;
      }
    },
    updateChatRequest({commit}, {job, userId, reasons, message, attractiveSkills, requestor, scoutMotivations}) {
      commit("setChatRequest", {job, userId, reasons, message, attractiveSkills, requestor, scoutMotivations})
    },
    updateDisplay({commit}, {userId, isDisplay}) {
      commit("setIsDisplay", {userId, isDisplay})
    }
  }
}
